import React, { useEffect, useState } from "react";
import { useLang } from "../i18n";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Leaf, Timer, AlertTriangle, Flame, ChevronDown } from "lucide-react";

function computeFasting(protocol, eatingStart) {
  const eatH = Number((protocol || "16:8").split(":")[1]) || 8;
  const [h, m] = (eatingStart || "12:00").split(":").map(Number);
  const now = new Date();
  const start = new Date(); start.setHours(h || 12, m || 0, 0, 0);
  const end = new Date(start.getTime() + eatH * 3600000);
  const eating = now >= start && now < end;
  let phase, target, since;
  if (eating) {
    phase = "eating"; target = end; since = start;
  } else {
    phase = "fasting";
    if (now < start) { target = start; since = new Date(end.getTime() - 24 * 3600000); }
    else { target = new Date(start.getTime() + 24 * 3600000); since = end; }
  }
  const msLeft = Math.max(target - now, 0);
  const fastedH = phase === "fasting" ? Math.max((now - since) / 3600000, 0) : 0;
  return { phase, msLeft, fastedH };
}

const fmt = (ms) => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

export function NutritionPanel() {
  const { t, lang } = useLang();
  const { user, refreshUser } = useAuth();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const enabled = user?.fasting_enabled;
  const protocol = user?.fasting_protocol || "16:8";
  const eatingStart = user?.eating_start || "12:00";

  const setFasting = async (payload) => {
    setBusy(true);
    try {
      await api.patch("/fasting", payload);
      await refreshUser();
    } catch (e) { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const acceptDisclaimer = async () => {
    setShowDisclaimer(false);
    await setFasting({ enabled: true, protocol, eating_start: eatingStart });
    toast.success(lang === "it" ? "Digiuno attivato" : "Fasting enabled");
  };

  const fasting = enabled ? computeFasting(protocol, eatingStart) : null;
  const autophagy = fasting && fasting.phase === "fasting" && fasting.fastedH >= 16;

  return (
    <div className="mb-8">
      {/* Mediterranean priority — max visual importance */}
      <div className="rounded-3xl p-7 bg-[#3F6E85] text-[#FDFBF7] relative overflow-hidden" data-testid="med-diet-card">
        <div className="grain opacity-30" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-10 h-10 rounded-xl bg-[#8FD9A8]/20 flex items-center justify-center">
              <Leaf className="w-5 h-5 text-[#8FD9A8]" />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#8FD9A8] font-semibold">{t("med_diet_priority")}</p>
              <h3 className="font-serif text-2xl tracking-tight">{t("med_diet_title")}</h3>
            </div>
          </div>
          <p className="text-[#c9d3cd] text-sm max-w-2xl">{t("med_diet_body")}</p>
        </div>
      </div>

      {/* Advanced options: intermittent fasting */}
      <div className="mt-3 rounded-2xl border border-stone-200 bg-white overflow-hidden">
        <button
          onClick={() => setShowAdvanced((s) => !s)}
          data-testid="advanced-toggle"
          className="w-full flex items-center justify-between px-5 py-3 text-sm text-[#6E6B65]"
        >
          <span className="flex items-center gap-2"><Timer className="w-4 h-4" /> {t("advanced_options")}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
        </button>

        {showAdvanced && (
          <div className="px-5 pb-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold text-[#2B4A59]">{t("fasting_title")}</p>
                <p className="text-sm text-[#6E6B65]">{t("fasting_desc")}</p>
              </div>
              {enabled ? (
                <Button variant="outline" onClick={() => setFasting({ enabled: false, protocol, eating_start: eatingStart })} disabled={busy} className="rounded-full shrink-0" data-testid="fasting-disable">
                  {t("fasting_active")} ✓
                </Button>
              ) : (
                <Button onClick={() => setShowDisclaimer(true)} disabled={busy} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] shrink-0" data-testid="fasting-enable">
                  {t("fasting_enable")}
                </Button>
              )}
            </div>

            {enabled && (
              <>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <label className="text-sm text-[#6E6B65]">
                    {t("protocol")}
                    <select value={protocol} onChange={(e) => setFasting({ enabled: true, protocol: e.target.value, eating_start: eatingStart })} data-testid="fasting-protocol" className="mt-1 w-full rounded-lg border border-stone-200 px-2 py-1.5 bg-white text-[#2B4A59]">
                      <option value="14:10">14:10</option>
                      <option value="16:8">16:8</option>
                      <option value="18:6">18:6</option>
                    </select>
                  </label>
                  <label className="text-sm text-[#6E6B65]">
                    {t("eating_start_label")}
                    <input type="time" value={eatingStart} onChange={(e) => setFasting({ enabled: true, protocol, eating_start: e.target.value })} data-testid="fasting-start" className="mt-1 w-full rounded-lg border border-stone-200 px-2 py-1.5 bg-white text-[#2B4A59]" />
                  </label>
                </div>

                {/* Autophagy timer — visible only when fasting active, secondary emphasis */}
                <div className={`mt-4 rounded-2xl p-5 border ${autophagy ? "border-[#E08A3C] bg-[#FBEDE7]" : "border-stone-200 bg-[#FBF9F4]"}`} data-testid="autophagy-timer">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {autophagy ? <Flame className="w-5 h-5 text-[#B5651D]" /> : <Timer className="w-5 h-5 text-[#3F6E85]" />}
                      <span className="font-semibold text-[#2B4A59]">
                        {autophagy ? t("autophagy_zone") : fasting.phase === "fasting" ? t("timer_fasting") : t("timer_eating")}
                      </span>
                    </div>
                    <span className="font-serif text-2xl text-[#2B4A59]" data-testid="autophagy-countdown">{fmt(fasting.msLeft)}</span>
                  </div>
                  <p className="text-xs text-[#6E6B65] mt-1">
                    {fasting.phase === "fasting"
                      ? `${fasting.fastedH.toFixed(1)}h ${t("hours_fasted")} · ${fmt(fasting.msLeft)} ${t("time_to_eat")}`
                      : `${fmt(fasting.msLeft)} ${t("time_to_close")}`}
                  </p>
                </div>
                <p className="text-xs text-[#6E6B65] mt-3 italic">{t("med_reminder")}</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Mandatory disclaimer modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="fasting-disclaimer">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-3 text-[#B7410E]">
              <AlertTriangle className="w-6 h-6 shrink-0" />
              <h3 className="font-serif text-2xl">{t("fasting_disclaimer_title")}</h3>
            </div>
            <p className="text-[#3a3a3a] mb-6 text-sm leading-relaxed">{t("fasting_disclaimer_body")}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowDisclaimer(false)} className="rounded-full" data-testid="disclaimer-cancel">{t("fasting_cancel")}</Button>
              <Button onClick={acceptDisclaimer} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] text-white" data-testid="disclaimer-accept">{t("fasting_accept")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
